import React, { useState } from 'react';
import { api, formatApiError } from '../../lib/api';
import { toast } from 'sonner';
import { Field } from './shared';
import { DateSelect } from '../../components/DateSelect';
import { AIWritingAssist } from '../../components/AIWritingAssist';
import { focalPosition } from '../../lib/photoFocal';
import ImageEditor from '../../components/ImageEditor';

export function ProfileTab({ memorial, onChange }) {
  // The "snapshot" of what's persisted server-side. Used to (a) detect
  // unsaved changes and (b) let the user discard local edits.
  const initial = React.useMemo(() => ({
    full_name: memorial.full_name || '',
    date_of_birth: memorial.date_of_birth || '',
    date_of_passing: memorial.date_of_passing || '',
    birthplace: memorial.birthplace || '',
    profile_photo_url: memorial.profile_photo_url || '',
    life_summary: memorial.life_summary || '',
    hero_tagline: memorial.hero_tagline || '',
    hometown: memorial.hometown || '',
    occupation: memorial.occupation || '',
    family_role: memorial.family_role || '',
    cemetery_name: memorial.cemetery_name || '',
    cemetery_address: memorial.cemetery_address || '',
    cemetery_city: memorial.cemetery_city || '',
    cemetery_region: memorial.cemetery_region || '',
    cemetery_country: memorial.cemetery_country || '',
    cemetery_section: memorial.cemetery_section || '',
    cemetery_lot: memorial.cemetery_lot || '',
  }), [memorial]);
  const [f, setF] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);
  // iter85 — Open the image editor before any profile-photo upload.
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorFile, setEditorFile] = useState(null);
  const BACKEND = process.env.REACT_APP_BACKEND_URL;

  // iter97.7 — Track unsaved local edits so the user can cancel before
  // anything hits the database.
  const dirty = React.useMemo(
    () => Object.keys(initial).some((k) => (f[k] || '') !== (initial[k] || '')),
    [f, initial],
  );

  // iter97.7 — Upload a new photo to the media bucket so we have a URL,
  // but DO NOT PATCH the memorial here. The new URL is staged on `f`
  // and only persisted when the user clicks "Save changes". Keeps the
  // user in control — they can discard or replace before committing.
  const uploadPhoto = async (file) => {
    setPhotoUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('visibility', 'public');
      fd.append('caption', 'Profile photo');
      const { data: media } = await api.post(`/memorials/${memorial.memorial_id}/media`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const url = `${BACKEND}/api/media/${media.media_id}/file`;
      setF((prev) => ({ ...prev, profile_photo_url: url }));
      toast.info('Photograph ready — click "Save changes" to apply.');
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setPhotoUploading(false);
    }
  };

  const save = async () => {
    if (!dirty) return;
    setSaving(true);
    try {
      await api.patch(`/memorials/${memorial.memorial_id}`, f);
      onChange?.();
      toast.success('Saved');
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSaving(false); }
  };

  // iter97.7 — Revert the local form to the last persisted snapshot.
  // The uploaded media row remains in the bucket (orphaned by design —
  // the family may delete it from the Media tab if they wish). The
  // memorial's portrait is untouched because we never PATCHed.
  const discard = () => {
    setF(initial);
    toast.success('Changes discarded');
  };

  return (
    <div
      className="ml-card space-y-6 [&_h2]:!text-[hsl(var(--primary))] [&_h2]:!font-bold [&_h3]:!text-[hsl(var(--primary))] [&_h3]:!font-bold [&_.ml-label]:!text-[hsl(var(--primary))] [&_.ml-label]:!font-bold [&_.ml-overline]:!text-[hsl(var(--primary))] [&_.ml-overline]:!font-bold"
      style={{ backgroundColor: '#F4E5C2' }}
      data-testid="profile-tab-panel"
    >
      <h2 className="font-serif text-2xl">Memorial profile</h2>

      {/* Profile photo — large, replaceable */}
      <div className="flex items-center gap-6 flex-wrap">
        <div className="w-28 h-28 rounded-full overflow-hidden bg-[hsl(var(--surface))] border border-[hsl(var(--primary)/0.12)] flex items-center justify-center">
          {f.profile_photo_url ? (
            <img
              src={f.profile_photo_url}
              alt={f.full_name}
              className="w-full h-full object-cover"
              data-testid="profile-photo-current"
            />
          ) : (
            <span className="font-serif italic text-[hsl(var(--primary)/0.4)] text-xs px-2 text-center">No photograph yet</span>
          )}
        </div>
        <div>
          <div className="ml-overline">Photograph</div>
          <div className="mt-2 flex items-center gap-3">
            <label
              className="inline-flex items-center justify-center gap-2 rounded-sm bg-white border border-[hsl(var(--primary))] text-[hsl(var(--primary))] px-6 py-2.5 text-[13px] font-semibold tracking-[0.04em] cursor-pointer transition-all duration-200 hover:bg-[hsl(var(--primary)/0.05)]"
              style={{ fontFamily: 'Inter, sans-serif' }}
              data-testid="profile-photo-replace-btn"
            >
              {photoUploading ? 'Uploading…' : (f.profile_photo_url ? 'Replace photograph' : 'Add a photograph')}
              <input
                type="file"
                accept="image/*"
                className="hidden"
                data-testid="profile-photo-input"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  setEditorFile(file);
                  setEditorOpen(true);
                  e.target.value = '';
                }}
              />
            </label>
          </div>
          <p className="text-[11px] text-[hsl(var(--muted-foreground))] mt-2 max-w-xs font-light">
            Appears on the memorial page, Legacy Tree heart, and every story video.
          </p>
        </div>
      </div>

      {/* iter94.8 — Pick a portrait from existing memorial photos.
          Lets families select an existing photo as the portrait
          without re-uploading. iter97.7 — Selection is now staged
          locally; persisted only when "Save changes" is clicked. */}
      <PortraitFromGalleryPicker
        memorialId={memorial.memorial_id}
        initialUrl={initial.profile_photo_url}
        currentUrl={f.profile_photo_url}
        onPicked={(url) => {
          setF((prev) => ({ ...prev, profile_photo_url: url }));
        }}
      />

      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Full name"><input className="ml-input" value={f.full_name} onChange={(e) => setF({ ...f, full_name: e.target.value })} data-testid="profile-fullname" /></Field>
        <Field label="Birthplace"><input className="ml-input" value={f.birthplace} onChange={(e) => setF({ ...f, birthplace: e.target.value })} /></Field>
        <Field label="Date of birth"><DateSelect value={f.date_of_birth || ''} max={f.date_of_passing || undefined} onChange={(v) => setF({ ...f, date_of_birth: v })} testIdPrefix="profile-dob" /></Field>
        <Field label="Date of passing"><DateSelect value={f.date_of_passing || ''} min={f.date_of_birth || undefined} max={new Date().toISOString().slice(0,10)} onChange={(v) => setF({ ...f, date_of_passing: v })} testIdPrefix="profile-dop" /></Field>
        <Field label="Hometown"><input className="ml-input" maxLength={120} value={f.hometown} onChange={(e) => setF({ ...f, hometown: e.target.value })} placeholder="Shown on the public About card" data-testid="profile-hometown" /></Field>
        <Field label="Occupation (optional)"><input className="ml-input" maxLength={120} value={f.occupation} onChange={(e) => setF({ ...f, occupation: e.target.value })} placeholder="e.g. Schoolteacher, Carpenter" data-testid="profile-occupation" /></Field>
        <Field label="Family role (optional)"><input className="ml-input" maxLength={80} value={f.family_role} onChange={(e) => setF({ ...f, family_role: e.target.value })} placeholder="e.g. Father, Mother, Sister, Friend" data-testid="profile-family-role" /></Field>
      </div>
      <Field label="Life summary">
        <textarea rows={5} className="ml-input" value={f.life_summary} onChange={(e) => setF({ ...f, life_summary: e.target.value })} />
        <AIWritingAssist
          context="life_summary"
          deceasedName={f.full_name}
          getCurrentText={() => f.life_summary || ''}
          onInsert={(text) => setF((prev) => ({ ...prev, life_summary: text }))}
          testIdPrefix="profile-ai-assist"
        />
      </Field>
      <Field label="Hero tagline (optional)">
        <input
          className="ml-input"
          maxLength={90}
          value={f.hero_tagline}
          onChange={(e) => setF({ ...f, hero_tagline: e.target.value.slice(0, 90) })}
          placeholder="A short line that appears under the name"
          data-testid="profile-hero-tagline"
        />
        <div className="mt-1.5 text-[11px] text-[hsl(var(--muted-foreground))] flex justify-between">
          <span>Leave empty to auto-generate from the Life summary.</span>
          <span className={f.hero_tagline.length > 80 ? 'text-[hsl(var(--accent))]' : ''}>
            {f.hero_tagline.length} / 90
          </span>
        </div>
      </Field>

      <div className="pt-4 mt-4 border-t border-[hsl(var(--primary)/0.08)]" data-testid="cemetery-section">
        <div className="ml-overline">Cemetery / resting place</div>
        <h3 className="font-serif text-xl mt-1">
          Where they rest
        </h3>
        <p className="text-xs text-[hsl(var(--muted-foreground))] mt-2 leading-relaxed max-w-md">
          Adding cemetery details powers the Legacy Map, so visitors can find
          where to pay their respects. All fields are optional.
        </p>
        <div className="mt-5 grid sm:grid-cols-2 gap-4">
          <Field label="Cemetery name">
            <input
              className="ml-input"
              maxLength={160}
              value={f.cemetery_name}
              onChange={(e) => setF({ ...f, cemetery_name: e.target.value })}
              data-testid="cemetery-name"
            />
          </Field>
          <Field label="Address line">
            <input
              className="ml-input"
              maxLength={240}
              value={f.cemetery_address}
              onChange={(e) => setF({ ...f, cemetery_address: e.target.value })}
              data-testid="cemetery-address"
            />
          </Field>
          <Field label="City">
            <input
              className="ml-input"
              maxLength={120}
              value={f.cemetery_city}
              onChange={(e) => setF({ ...f, cemetery_city: e.target.value })}
              data-testid="cemetery-city"
            />
          </Field>
          <Field label="State / region">
            <input
              className="ml-input"
              maxLength={120}
              value={f.cemetery_region}
              onChange={(e) => setF({ ...f, cemetery_region: e.target.value })}
              data-testid="cemetery-region"
            />
          </Field>
          <Field label="Country">
            <input
              className="ml-input"
              maxLength={120}
              value={f.cemetery_country}
              onChange={(e) => setF({ ...f, cemetery_country: e.target.value })}
              data-testid="cemetery-country"
            />
          </Field>
          <Field label="Section / lot">
            <input
              className="ml-input"
              maxLength={120}
              value={[f.cemetery_section, f.cemetery_lot].filter(Boolean).join(' · ') || ''}
              onChange={(e) => {
                // Split on middle dot, em-dash, or comma for convenience.
                const parts = e.target.value.split(/[·\-,]/).map((s) => s.trim());
                setF({
                  ...f,
                  cemetery_section: parts[0] || '',
                  cemetery_lot: parts[1] || '',
                });
              }}
              placeholder="e.g. Section B · Lot 147"
              data-testid="cemetery-section-lot"
            />
          </Field>
        </div>
      </div>

      {/* iter97.7 — Sticky action bar at the bottom. Shows an "Unsaved
          changes" indicator while the user has edits not yet persisted,
          and a Discard button to revert everything (including a freshly
          picked portrait). */}
      <div
        className="flex items-center gap-3 flex-wrap pt-2 border-t border-[hsl(var(--primary)/0.10)]"
        data-testid="profile-action-bar"
      >
        <button
          onClick={save}
          disabled={saving || !dirty}
          className="ml-btn-primary disabled:opacity-50"
          data-testid="profile-save-btn"
        >
          {saving ? 'Saving…' : 'Save changes'}
        </button>
        <button
          type="button"
          onClick={discard}
          disabled={!dirty || saving}
          className="text-[11px] tracking-[0.18em] uppercase font-medium text-[hsl(var(--primary)/0.7)] hover:text-[hsl(var(--destructive))] disabled:opacity-30 disabled:cursor-not-allowed"
          data-testid="profile-discard-btn"
        >
          Discard changes
        </button>
        {dirty && (
          <span
            className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[hsl(var(--accent))] inline-flex items-center gap-1.5"
            data-testid="profile-unsaved-indicator"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--accent))]" />
            Unsaved changes
          </span>
        )}
      </div>

      <ImageEditor
        open={editorOpen}
        file={editorFile}
        aspect={1}
        title="Adjust the photograph"
        saveLabel="Save photograph"
        onCancel={() => { setEditorOpen(false); setEditorFile(null); }}
        onSave={(edited) => {
          setEditorOpen(false);
          uploadPhoto(edited);
          setEditorFile(null);
        }}
      />
    </div>
  );
}


// iter94.8 — Inline "use a photo you've already uploaded" picker. Shows
// a small grid of existing public photos. Click → PATCHes the memorial
// row's `profile_photo_url` to that media's serve URL. No re-upload.
function PortraitFromGalleryPicker({ memorialId, initialUrl, currentUrl, onPicked }) {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  // iter94.8 — Snapshot the portrait URL the page was loaded with so
  // the parent's refresh() (which re-passes `initialUrl`) cannot retire
  // the regen CTA before the family clicks it. Captured once on mount.
  const [sessionInitialUrl] = useState(initialUrl || '');
  // iter94.8 — When the portrait has changed during this session, offer
  // to regenerate the Life Journey video so its opening frame matches.
  const [regenBusy, setRegenBusy] = useState(false);
  const [regenDone, setRegenDone] = useState(false);
  const BACKEND = process.env.REACT_APP_BACKEND_URL;

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(
          `/memorials/${memorialId}/media?visibility=public`,
        );
        if (cancelled) return;
        const images = (data.media || []).filter((m) => m.kind !== 'video');
        setPhotos(images);
      } catch (e) {
        if (!cancelled) toast.error(formatApiError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [memorialId]);

  const photoUrl = (m) =>
    m.external_url || `${BACKEND}/api/media/${m.media_id}/file`;

  const isCurrent = (m) => {
    const u = photoUrl(m);
    return !!currentUrl && (currentUrl === u || currentUrl.endsWith(`/media/${m.media_id}/file`));
  };

  // iter97.7 — Just stage the picked photo. The parent's "Save changes"
  // button commits it via a single PATCH alongside any other edits. No
  // database write here, so users can freely preview different photos
  // and discard the change if they don't like the result.
  const setAsPortrait = (m) => {
    const url = photoUrl(m);
    onPicked?.(url);
    // Re-arm the regen CTA on each successful pick.
    setRegenDone(false);
  };

  const regenerateVideo = async () => {
    setRegenBusy(true);
    try {
      // Default to the animated portrait-tribute style (Style B). For
      // slideshow-style videos the family explicitly regenerates from
      // the Life Journey tab — this CTA is specifically about the
      // opening portrait frame, which lives in the animated style.
      await api.post(`/memorials/${memorialId}/video/generate?style=animated`);
      setRegenDone(true);
      toast.success(
        'Video render queued — give it a couple of minutes. You can leave this page.',
      );
    } catch (e) {
      toast.error(formatApiError(e, 'Could not start the video render'));
    } finally {
      setRegenBusy(false);
    }
  };

  if (loading) return null;
  if (photos.length === 0) return null;

  // The CTA only appears when the portrait differs from what was
  // loaded with the page (so we don't nag on every page view).
  const portraitChanged =
    !!currentUrl && currentUrl !== sessionInitialUrl;

  return (
    <div
      data-testid="portrait-gallery-picker"
      className="rounded-md border border-[hsl(var(--primary)/0.14)] bg-[#FDFBF7] px-5 py-4"
    >
      <div className="text-[11px] tracking-[0.22em] uppercase font-semibold text-[hsl(var(--primary)/0.7)] mb-1">
        Or pick from your uploaded photos
      </div>
      <p className="text-[12px] text-[hsl(var(--primary)/0.7)] mb-3 leading-relaxed">
        Click any photo to set it as the portrait. The Memories gallery and slideshow are not affected.
      </p>
      <div className="grid grid-cols-5 sm:grid-cols-7 md:grid-cols-9 gap-2">
        {photos.map((m) => {
          const url = photoUrl(m);
          const current = isCurrent(m);
          return (
            <button
              key={m.media_id}
              type="button"
              onClick={() => !current && setAsPortrait(m)}
              disabled={current}
              data-testid={`portrait-gallery-photo-${m.media_id}`}
              aria-label={current ? 'Currently used as portrait' : 'Use this photo as portrait'}
              className={`relative aspect-square rounded-full overflow-hidden border transition-all focus:outline-none focus:ring-2 focus:ring-[hsl(var(--accent))]/50 group ${
                current
                  ? 'border-[hsl(var(--accent))] ring-2 ring-[hsl(var(--accent))]/40 cursor-default'
                  : 'border-[hsl(var(--primary)/0.15)] hover:border-[hsl(var(--accent))]'
              }`}
              title={current ? 'Currently the portrait' : 'Use as portrait'}
            >
              <img src={url} alt="" className="w-full h-full object-cover" style={{ objectPosition: focalPosition(m) }} loading="lazy" />
              {current && (
                <span className="absolute inset-x-0 bottom-0 text-[8px] tracking-[0.18em] uppercase bg-[hsl(var(--accent))] text-white text-center py-0.5 font-semibold">
                  Portrait
                </span>
              )}
              {!current && (
                <span className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/45 transition-all">
                  <span className="opacity-0 group-hover:opacity-100 text-[9px] tracking-[0.18em] uppercase text-white font-semibold transition-opacity">
                    Use as portrait
                  </span>
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* iter94.8 — "Regenerate video with this portrait" CTA. Appears
          ONLY after the portrait actually changed during this session,
          so unchanged-portrait pages stay calm. Re-arms after each
          fresh pick (regenDone resets above). */}
      {portraitChanged && (
        <div
          data-testid="portrait-regen-cta"
          className="mt-4 pt-4 border-t border-[hsl(var(--primary)/0.1)] flex items-center justify-between gap-4 flex-wrap"
        >
          <div className="text-[12px] text-[hsl(var(--primary)/0.75)] leading-relaxed max-w-md">
            <span className="font-semibold text-[hsl(var(--primary))]">Heads up — </span>
            the Life Journey video uses this portrait as its opening frame. Rebuild the video now so it matches?
          </div>
          <button
            type="button"
            onClick={regenerateVideo}
            disabled={regenBusy || regenDone}
            data-testid="portrait-regen-btn"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[hsl(var(--accent))] text-white text-[12px] font-semibold tracking-[0.04em] hover:opacity-90 disabled:opacity-60 transition"
          >
            {regenBusy
              ? 'Queuing…'
              : regenDone
              ? 'Render queued ✓'
              : 'Regenerate video'}
          </button>
        </div>
      )}
    </div>
  );
}
